/**
 * RoleNavigationPage Component
 *
 * Displays organisation and role selection for users with
 * multiple organisations or roles.
 *
 * This page is shown after authentication when:
 * - User belongs to multiple organisations
 * - User has multiple roles in a single organisation
 * - User is an admin AND has org memberships
 */

import { useState } from 'react';
import { useAuth } from '../auth';
import { useUserOrganisations } from '../hooks';
import { OrganisationCard } from '../components/OrganisationCard';
import { Loading } from '../components';
import './RoleNavigationPage.css';

/**
 * RoleNavigationPage Component
 *
 * Shows a selection interface for users with multiple organisations/roles.
 * Handles navigation to downstream apps with token passing.
 */
export function RoleNavigationPage() {
  const { user, logout } = useAuth();
  const { isLoading, error, decision } = useUserOrganisations();
  const [isRedirecting, setIsRedirecting] = useState(false);

  /**
   * Handle role selection - navigates to the appropriate app
   */
  const handleSelectRole = (orgSlug: string, role: string) => {
    setIsRedirecting(true);

    const baseUrls: Record<string, string> = {
      admin: import.meta.env.VITE_ADMIN_APP_URL || 'https://admin.hirenow.com',
      partner: import.meta.env.VITE_PARTNER_APP_URL || 'https://partner.hirenow.com',
      supplier:
        import.meta.env.VITE_SUPPLIER_APP_URL || 'https://supplier.hirenow.com',
      hirer: import.meta.env.VITE_HIRER_APP_URL || 'https://hirer.hirenow.com',
    };

    const baseUrl = baseUrls[role.toLowerCase()] || baseUrls.hirer;
    const targetUrl = `${baseUrl}/${orgSlug}`;

    // Include token for cross-origin auth (via URL fragment to avoid logging)
    if (user?.idToken) {
      window.location.href = `${targetUrl}#token=${encodeURIComponent(user.idToken)}`;
    } else {
      window.location.href = targetUrl;
    }
  };

  /**
   * Handle admin-only access (no org context needed)
   */
  const handleAdminAccess = () => {
    setIsRedirecting(true);
    const adminUrl =
      import.meta.env.VITE_ADMIN_APP_URL || 'https://admin.hirenow.com';

    if (user?.idToken) {
      window.location.href = `${adminUrl}#token=${encodeURIComponent(user.idToken)}`;
    } else {
      window.location.href = adminUrl;
    }
  };

  /**
   * Handle logout
   */
  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  // Loading state
  if (isLoading) {
    return <Loading fullScreen message="Loading your organisations..." />;
  }

  // Redirecting state
  if (isRedirecting) {
    return <Loading fullScreen message="Redirecting..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="nav-page nav-page-error">
        <h1>Something went wrong</h1>
        <p>{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="nav-btn-primary"
        >
          Try Again
        </button>
        <button onClick={handleLogout} className="nav-btn-secondary">
          Sign Out
        </button>
      </div>
    );
  }

  // No access state
  if (decision?.type === 'no-access') {
    return (
      <div className="nav-page nav-page-error">
        <h1>No Access</h1>
        <p>{decision.reason}</p>
        <button onClick={handleLogout} className="nav-btn-secondary">
          Sign Out
        </button>
      </div>
    );
  }

  // Selection page - extract data from decision
  const organisations =
    decision?.type === 'show-selection' ? decision.organisations : [];
  const isAdmin =
    decision?.type === 'show-selection' ? decision.isAdmin : false;

  return (
    <div className="nav-page">
      <header className="nav-header">
        <div className="nav-logo">
          <img src="/logo.svg" alt="Hire Now" height="32" />
        </div>
        <button onClick={handleLogout} className="nav-logout-btn">
          Sign Out
        </button>
      </header>

      <main className="nav-content">
        <h1 className="nav-title">
          Welcome{user?.displayName ? `, ${user.displayName}` : ''}!
        </h1>
        <p className="nav-subtitle">Select where you'd like to go:</p>

        <div className="nav-cards">
          {/* Admin access card (if admin) */}
          {isAdmin && (
            <div className="org-card org-card-admin">
              <h3 className="org-card-title">Platform Administration</h3>
              <p className="org-card-slug">System-wide access</p>
              <div className="org-card-roles">
                <button
                  className="role-button role-admin"
                  onClick={handleAdminAccess}
                  disabled={isRedirecting}
                >
                  <span className="role-icon" aria-hidden="true">
                    ⚙️
                  </span>
                  <span className="role-label">Admin Console</span>
                </button>
              </div>
            </div>
          )}

          {/* Organisation cards */}
          {organisations.map((org) => (
            <OrganisationCard
              key={org.organisation_id}
              organisation={org}
              onSelectRole={handleSelectRole}
              isLoading={isRedirecting}
            />
          ))}
        </div>
      </main>

      <footer className="nav-footer">
        <p>
          Need help?{' '}
          <a href="mailto:support@hirenow.com">Contact Support</a>
        </p>
      </footer>
    </div>
  );
}
